import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useLocaleFmt } from "@/lib/i18n/format";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { adminListAuditLog } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/app/admin/audit")({
  beforeLoad: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: AuditPage,
});

function AuditPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => { adminListAuditLog({ data: {} }).then(setRows); }, []);
  return (
    <>
      <PageHeader title={t("pages.admin.audit.title")} description={t("pages.admin.audit.description")} />
      <PageBody>
        {!rows ? <Loader2 className="h-5 w-5 animate-spin" /> : rows.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground">{t("pages.admin.audit.empty")}</Card>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <Card key={r.id} className="p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{r.action}</Badge>
                  <span className="text-xs text-muted-foreground">{r.entity}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString(localeTag)}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {t("pages.admin.audit.actor")}: <span className="font-mono">{r.actor_id?.slice(0, 8) ?? "-"}</span>
                  {r.target_user_id && <> · {t("pages.admin.audit.target")}: <span className="font-mono">{r.target_user_id.slice(0, 8)}</span></>}
                  {r.ip && <> · {t("pages.admin.audit.ip")}: {r.ip}</>}
                </div>
                {(r.before || r.after) && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-accent">{t("pages.admin.audit.details")}</summary>
                    <pre className="mt-1 max-h-60 overflow-auto rounded bg-muted/40 p-2 text-[10px]">{JSON.stringify({ before: r.before, after: r.after }, null, 2)}</pre>
                  </details>
                )}
              </Card>
            ))}
          </div>
        )}
      </PageBody>
    </>
  );
}
